import React, { useState, useEffect } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { Info } from "lucide-react";

/**
 * Transaction Type Widget
 * Modernized with shadcn components - Shows commission earned with month selector
 */
function Transactions_Type({ transactionItemName, transactionItemCount, commissionData, secretK }) {
    const [commissionCount, setCommissionCount] = useState('');
    const [filteredDataSelect, setFilteredDataSelect] = useState([]);
    const [selectedValue, setSelectedValue] = useState('');

    useEffect(() => {
        if (commissionData && commissionData.length > 0) {
            const total_comm = commissionData[0].DistComm + commissionData[0].SubDistComm;
            const formatted = total_comm !== 0 ? total_comm.toFixed(2) : '0.00';
            setCommissionCount(formatted);

            const convertedData = commissionData.map((data) => ({
                month: data.Date_Month.split(',')[0],
                year: data.Date_Month.split(',')[1],
                fullDate: data.Date_Month
            }));
            setFilteredDataSelect(convertedData);
            // Set the initial selected value at the same time so the Select shows it immediately
            setSelectedValue(convertedData[0]?.fullDate || '');
        }
    }, [commissionData]);

    const updateCommission = (value) => {
        setSelectedValue(value);
        const filteredCommission = commissionData.filter((data) => data.Date_Month === value);
        if (filteredCommission.length > 0) {
            const total_ucomm = filteredCommission[0].DistComm + filteredCommission[0].SubDistComm;
            setCommissionCount(total_ucomm.toFixed(2));
        }
    };

    if (transactionItemName === 'Commission Earned') {
        return (
            <div className="flex h-full flex-col gap-2">
                <div className="flex items-start justify-between gap-2">
                    <span className="text-sm font-medium text-primary/70">Commission Earned</span>
                    <TooltipProvider>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6 shrink-0 text-primary/50 hover:bg-transparent hover:text-accent"
                                >
                                    <Info className="h-4 w-4" />
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                                <p className="text-sm">
                                    Commission Earned is shown for the previous month and is normally available by the 20th of the current month.
                                </p>
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                </div>
                <div className="text-3xl font-bold text-primary">
                    ${commissionCount || '0.00'}
                </div>
                {commissionData && commissionData.length > 0 ? (
                    <Select onValueChange={updateCommission} value={selectedValue}>
                        <SelectTrigger className="mt-auto h-8 w-full px-2 py-1.5 text-xs">
                            <SelectValue placeholder="Select month" />
                        </SelectTrigger>
                        <SelectContent>
                            {filteredDataSelect
                                .filter(filterItem => filterItem.fullDate && filterItem.fullDate.trim() !== '')
                                .map((filterItem, index) => (
                                    <SelectItem key={index} value={filterItem.fullDate}>
                                        {filterItem.month} {filterItem.year}
                                    </SelectItem>
                                ))}
                        </SelectContent>
                    </Select>
                ) : null}
            </div>
        );
    }

    // For other transaction types (direct_rxs, direct_non_rxs, override_rxs, override_non-rxs)
    let displayName = '';
    if (transactionItemName === 'direct_rxs') displayName = 'Direct Transactions (PPBM)';
    if (transactionItemName === 'direct_non_rxs') displayName = 'Direct Transactions (SPBM)';
    if (transactionItemName === 'override_rxs') displayName = 'Override Transactions (PPBM)';
    if (transactionItemName === 'override_non-rxs') displayName = 'Override Transactions (SPBM)';

    return (
        <div className="flex h-full flex-col gap-2">
            {displayName && (
                <p className="text-sm font-medium text-primary/70">
                    {displayName.split('(')[0]}<br />({displayName.split('(')[1]}
                </p>
            )}
            <div className="text-3xl font-bold text-primary">
                {transactionItemCount || 0}
            </div>
        </div>
    );
}

export default Transactions_Type;
